import { round2 } from "../household/moneyInput";
import { paymentDate, ymdToISO } from "../converters/paymentDates";
import type { YMD } from "../converters/dates";

export interface LoanRow { month: number; payment: number; interest: number; principal: number; balance: number }
export interface LoanPlan {
  standardPayment: number;
  rows: LoanRow[];
  totalInterest: number;
  totalPaid: number;
  payoffMonths: number;
}
export interface LoanInput { principal: number; aprPercent: number; years: number; extraMonthly?: number; lumpSum?: number; lumpMonth?: number }

/** Level monthly payment for the standard term, rounded to cents. */
export function standardPayment(principal: number, aprPercent: number, years: number): number {
  const n = Math.round(years * 12);
  const r = aprPercent / 100 / 12;
  if (n <= 0) return 0;
  return round2(r === 0 ? principal / n : (principal * r) / (1 - Math.pow(1 + r, -n)));
}

/** Month-by-month schedule, interest rounded to cents each month, last payment trimmed to the balance. */
export function buildPlan(i: LoanInput): LoanPlan {
  const r = i.aprPercent / 100 / 12;
  const std = standardPayment(i.principal, i.aprPercent, i.years);
  const pay = round2(std + (i.extraMonthly ?? 0));
  const lump = i.lumpSum ?? 0;
  const lumpMonth = Math.max(1, Math.round(i.lumpMonth ?? 1));
  const rows: LoanRow[] = [];
  let balance = round2(i.principal);
  let totalInterest = 0;
  let totalPaid = 0;
  const cap = 1200;
  while (balance > 0 && rows.length < cap) {
    const month = rows.length + 1;
    const interest = round2(balance * r);
    let pmt = pay + (month === lumpMonth ? lump : 0);
    // Final standard payment absorbs rounding so the balance lands on exactly zero.
    if (month === Math.round(i.years * 12) && !(i.extraMonthly || lump)) pmt = round2(balance + interest);
    pmt = Math.min(pmt, round2(balance + interest));
    const principalPaid = round2(pmt - interest);
    balance = round2(balance - principalPaid);
    totalInterest = round2(totalInterest + interest);
    totalPaid = round2(totalPaid + pmt);
    rows.push({ month, payment: pmt, interest, principal: principalPaid, balance });
  }
  return { standardPayment: std, rows, totalInterest, totalPaid, payoffMonths: rows.length };
}

/** CSV rows. With `first` (the first payment date) a "Payment date" column follows the month number; without it the columns are as before. */
export function planToCsvRows(rows: LoanRow[], first?: YMD): (string | number)[][] {
  const dated = !!first;
  return [
    ["Month", ...(dated ? ["Payment date"] : []), "Payment", "Interest", "Principal", "Remaining balance"],
    ...rows.map((r) => [r.month, ...(dated ? [ymdToISO(paymentDate(first!, r.month - 1))] : []), r.payment.toFixed(2), r.interest.toFixed(2), r.principal.toFixed(2), r.balance.toFixed(2)]),
  ];
}
